// The staff invoice page's item list and the client's PO number (Stephanie
// 2026-11-02: "the invoice is now there but there are no items, only the
// total. Also, i need a place to put the client's PO#").
//   • Items: GET /invoices/:id/items — each amount comes from the same math
//     that made the invoice, so the list adds up to the subtotal. (No "the quote
//     has changed" note — Stephanie 2026-11-03.)
//   • Client PO #: kept on the quote / order (orders.customer_po), so the
//     client's invoice, the quote and QuickBooks all show the same number.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiErrorMessage } from "@/lib/api";
import type { InvoiceTargets } from "@/lib/invoice-targets";
import { RemoveFromInvoiceButton } from "@/components/invoices/RemoveFromInvoiceButton";

interface ItemsResp {
  items: Array<{ id: string; name: string; imageUrl: string | null; units: number; amount: string }>;
  changed: boolean;
}

function money(s: string): string {
  const n = Number(s);
  return Number.isFinite(n) ? n.toLocaleString("en-US", { style: "currency", currency: "USD" }) : s;
}

export function InvoiceItemsCard({ invoiceId, orderId }: { invoiceId: string; orderId: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["invoices", invoiceId, "items"],
    queryFn: () => api<ItemsResp>(`/invoices/${invoiceId}/items`),
  });
  // Same rule as the quote's bin: only while the invoice is unpaid, not in
  // QuickBooks, etc. (the server says; no bin otherwise). Stephanie
  // 2026-11-02: "how would you delete an item from an invoice if you dont even
  // list the item?"
  const targets = useQuery({
    queryKey: ["orders", orderId, "invoice-targets"],
    queryFn: () => api<InvoiceTargets>(`/orders/${orderId}/invoice-targets`),
  });
  const rows = q.data?.items ?? [];
  return (
    <section className="rounded-md border border-rule bg-white p-[22px]" aria-labelledby="invoice-items-title">
      <h2 id="invoice-items-title" className="text-lg font-semibold tracking-[-0.4px] text-ink">Items</h2>
      {q.isLoading ? (
        <div className="mt-3 h-12 animate-pulse rounded bg-cream-50" aria-label="Loading items" />
      ) : q.isError ? (
        <p className="mt-3 text-sm text-rust-600">Couldn't load the items. Reload the page to try again.</p>
      ) : rows.length === 0 ? (
        <p className="mt-3 text-sm text-muted">No items on this invoice's quote.</p>
      ) : (
        <table className="mt-3 w-full text-sm">
          <thead>
            <tr className="border-b border-[#e7e7e4] text-left text-[11px] font-medium text-muted">
              <th className="py-2 font-medium">Item</th>
              <th className="py-2 text-right font-medium">Units</th>
              <th className="py-2 text-right font-medium">Amount</th>
              <th className="w-8 py-2"><span className="sr-only">Remove</span></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#e7e7e4]">
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="py-2.5 text-ink">
                  <span className="flex items-center gap-3">
                    <span className="h-10 w-10 flex-shrink-0 overflow-hidden rounded border border-[#e7e7e4] bg-cream-50">
                      {r.imageUrl ? <img src={r.imageUrl} alt="" loading="lazy" className="h-full w-full object-cover" /> : null}
                    </span>
                    {r.name}
                  </span>
                </td>
                <td className="py-2.5 text-right tabular-nums text-ink">{r.units.toLocaleString()}</td>
                <td className="py-2.5 text-right tabular-nums text-ink">{money(r.amount)}</td>
                <td className="py-2.5 pl-3 text-right">
                  <RemoveFromInvoiceButton
                    orderId={orderId}
                    targets={targets.data}
                    line={{ id: r.id, name: r.name }}
                    onDone={() => {
                      void qc.invalidateQueries({ queryKey: ["invoices"] });
                      void qc.invalidateQueries({ queryKey: ["orders", orderId] });
                      void targets.refetch();
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

export function ClientPoField({ orderId, invoiceId, value }: { orderId: string; invoiceId: string; value: string | null }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (po: string | null) => api(`/orders/${orderId}`, { method: "PATCH", body: JSON.stringify({ customer_po: po }) }),
    onSuccess: () => {
      setDraft(null);
      setError(null);
      void qc.invalidateQueries({ queryKey: ["invoices", invoiceId, "detail"] });
      void qc.invalidateQueries({ queryKey: ["orders", orderId, "detail"] });
    },
    onError: (e) => setError(apiErrorMessage(e, "We couldn't save the PO number. Try again.")),
  });

  if (draft === null) {
    return (
      <span className="inline-flex items-baseline gap-2">
        {value ? <span>{value}</span> : <span className="font-normal text-muted">None</span>}
        <button type="button" onClick={() => { setError(null); setDraft(value ?? ""); }} className="text-xs font-medium text-muted hover:text-ink">
          {value ? "Edit" : "Add"}
        </button>
      </span>
    );
  }
  return (
    <form
      className="flex flex-col items-end gap-1"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate(draft.trim() || null);
      }}
    >
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        autoFocus
        maxLength={100}
        aria-label="Client PO number"
        placeholder="e.g. 4500123"
        className="h-8 w-40 rounded-md border border-rule bg-white px-2 text-right text-sm font-normal text-ink"
      />
      <span className="flex gap-2">
        <button type="submit" disabled={save.isPending} className="rounded-md bg-ink px-2.5 py-1 text-xs font-medium text-white disabled:opacity-50">
          {save.isPending ? "Saving…" : "Save"}
        </button>
        <button type="button" onClick={() => setDraft(null)} className="px-1 text-xs text-ink">Cancel</button>
      </span>
      {error && <span className="text-xs font-normal text-rust-600">{error}</span>}
    </form>
  );
}
